import { useMemo, useState } from 'react';
import { ExternalProperty } from 'crono-fe-common/types/externalProperty';
import { ExternalValue } from 'crono-fe-common/types/externalValue';
import { StyledEPWrapper } from './style';
import {
  getExternalPropertyLabels,
  getExternalPropertyOptions,
  parseDateValueFromMultipleFormats,
} from 'crono-fe-common/utils';
import IntegrationType from 'crono-fe-common/types/enums/integrationType';
import { useAuth } from 'context/auth';
import { ExternalPropertyTextInput } from './ExternalPropertyTextInput';
import {
  InputDatePicker,
  Switch,
  Combobox,
  ComboboxInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxList,
  ComboboxItem,
  SelectMulti,
  SelectMultiTrigger,
  SelectMultiValue,
  SelectMultiContent,
  SelectMultiGroup,
  SelectMultiItem,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@crono-one/design-system';
import { FeConstants } from 'constants/FeConstants';
import { getDateYearsFromNow, setToMidDayOfPassedDate } from 'utils/dateUtils';
import DateTimeInput from './DateTimeInput';
import CommittableInput from './CommittableInput';
import { useCloseOnScroll } from 'hooks/useCloseOnScroll';

export interface InputExternalPropertyNotTagProps {
  externalValue?:
    | ExternalValue
    | { externalPropertyId: number; value: string }
    | null;
  externalProperty: ExternalProperty;
  owned: boolean;
  handleSetValue: (externalPropertyId: number, value: string) => void;
  bypassDisabled?: boolean;
  showLabel?: boolean;
  formBoxStyle?: React.CSSProperties;
  style?: React.CSSProperties;
  textareaHeight?: number;
  onHeightChange?: (height: number) => void;
  /** Closes the date picker when this element scrolls. */
  scrollContainer?: HTMLElement | null;
}

const InputExternalPropertyNotTag = ({
  externalValue,
  externalProperty,
  owned,
  handleSetValue,
  bypassDisabled = false,
  showLabel = true,
  formBoxStyle,
  style,
  textareaHeight,
  onHeightChange,
  scrollContainer,
}: InputExternalPropertyNotTagProps) => {
  const { user } = useAuth();
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  useCloseOnScroll(scrollContainer, isDatePickerOpen, () =>
    setIsDatePickerOpen(false),
  );

  const disabled = !bypassDisabled && (!owned || !externalProperty.isEditable);

  const showNotEditablePlaceholder: string | null = !externalProperty.isEditable
    ? 'Not editable'
    : null;

  const component = useMemo(() => {
    const tooltipMessage: string | undefined = !owned
      ? 'You are not the owner'
      : !externalProperty.isEditable
        ? 'This field is not editable'
        : undefined;

    switch (externalProperty.valueType) {
      case 'String':
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <CommittableInput
                  type="text"
                  placeholder={showNotEditablePlaceholder ?? 'Enter a value'}
                  disabled={disabled}
                  value={externalValue?.value ?? ''}
                  onCommit={(value) =>
                    handleSetValue(externalProperty.id, value)
                  }
                />
              </div>
            </TooltipTrigger>
            {tooltipMessage && (
              <TooltipContent side="bottom">{tooltipMessage}</TooltipContent>
            )}
          </Tooltip>
        );

      case 'Text':
        return (
          <ExternalPropertyTextInput
            value={externalValue?.value ?? ''}
            disabled={disabled}
            tooltipMessage={tooltipMessage}
            placeholder={showNotEditablePlaceholder ?? 'Enter a text'}
            renderHtml={!!externalProperty.renderHtml}
            label={externalProperty.publicName}
            minRows={5}
            maxRows={5}
            height={
              textareaHeight !== undefined ? `${textareaHeight}px` : undefined
            }
            onHeightChange={onHeightChange}
            save={(v) => handleSetValue(externalProperty.id, v)}
          />
        );

      case 'MultiSelect': {
        const value = externalValue?.value?.split(';') || [];
        const options = getExternalPropertyOptions(externalProperty);
        const labels = getExternalPropertyLabels(externalProperty);
        const optionsObjects = options.map((option, index) => ({
          value: option,
          label: labels[index] ?? '',
        }));
        const filteredValues = value.filter((value) => options.includes(value));

        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <SelectMulti
                  items={optionsObjects}
                  value={filteredValues}
                  onValueChange={(value) => {
                    handleSetValue(externalProperty.id, value.join(';'));
                  }}
                >
                  <SelectMultiTrigger
                    className="w-full"
                    size="sm"
                    disabled={disabled}
                  >
                    <SelectMultiValue placeholder="Select property..." />
                  </SelectMultiTrigger>
                  <SelectMultiContent positionerClassname="z-2000">
                    <SelectMultiGroup>
                      {optionsObjects.map((option) => (
                        <SelectMultiItem
                          key={option.value}
                          value={option.value}
                        >
                          {option.label}
                        </SelectMultiItem>
                      ))}
                    </SelectMultiGroup>
                  </SelectMultiContent>
                </SelectMulti>
              </div>
            </TooltipTrigger>
            {tooltipMessage && (
              <TooltipContent side="bottom">{tooltipMessage}</TooltipContent>
            )}
          </Tooltip>
        );
      }

      case 'Numeric':
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <CommittableInput
                  type="number"
                  placeholder={
                    showNotEditablePlaceholder ?? 'Enter a numeric value'
                  }
                  disabled={disabled}
                  value={externalValue?.value ?? ''}
                  onCommit={(value) =>
                    handleSetValue(externalProperty.id, value)
                  }
                />
              </div>
            </TooltipTrigger>
            {tooltipMessage && (
              <TooltipContent side="bottom">{tooltipMessage}</TooltipContent>
            )}
          </Tooltip>
        );

      case 'List': {
        const value = externalValue?.value || '';
        const options = getExternalPropertyOptions(externalProperty);
        const labels = getExternalPropertyLabels(externalProperty);
        const selectedLabel = value
          ? labels[options.indexOf(value)]
          : undefined;

        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Combobox
                  value={{
                    value,
                    label: selectedLabel,
                  }}
                  items={options.map((option, index) => ({
                    value: option,
                    label: labels[index] ?? '',
                  }))}
                  onValueChange={(value) => {
                    if (!value) {
                      handleSetValue(externalProperty.id, '');
                      return;
                    }
                    handleSetValue(externalProperty.id, value.value);
                  }}
                >
                  <ComboboxInput
                    placeholder={
                      showNotEditablePlaceholder ?? 'Add information...'
                    }
                    className="w-full"
                    dsSize="sm"
                    disabled={disabled}
                  />
                  <ComboboxContent positionerClassname="z-50000">
                    <ComboboxEmpty>No items found.</ComboboxEmpty>
                    <ComboboxList>
                      {(item) => (
                        <ComboboxItem key={item.value} value={item}>
                          {item.label}
                        </ComboboxItem>
                      )}
                    </ComboboxList>
                  </ComboboxContent>
                </Combobox>
              </div>
            </TooltipTrigger>
            {tooltipMessage && (
              <TooltipContent side="bottom">{tooltipMessage}</TooltipContent>
            )}
          </Tooltip>
        );
      }

      case 'Boolean':
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <Switch
                  aria-label={externalProperty.publicName}
                  checked={externalValue?.value === 'true'}
                  onCheckedChange={(checked) =>
                    handleSetValue(
                      externalProperty.id,
                      checked ? 'true' : 'false',
                    )
                  }
                  disabled={disabled}
                />
              </div>
            </TooltipTrigger>
            {tooltipMessage && (
              <TooltipContent side="bottom">{tooltipMessage}</TooltipContent>
            )}
          </Tooltip>
        );

      case 'Date':
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <InputDatePicker
                  contentClassName="z-100000"
                  triggerClassName="w-full"
                  size="sm"
                  mode="single"
                  open={isDatePickerOpen}
                  onOpenChange={setIsDatePickerOpen}
                  timeZone="UTC"
                  selected={
                    parseDateValueFromMultipleFormats(externalValue?.value) ??
                    undefined
                  }
                  disabled={disabled}
                  onSelect={(value) => {
                    if (!value) return;
                    handleSetValue(
                      externalProperty.id,
                      user?.company?.integrationType === IntegrationType.HUBSPOT
                        ? (parseDateValueFromMultipleFormats(
                            value,
                            true,
                          ) as unknown as string)
                        : (setToMidDayOfPassedDate(value) as unknown as string),
                    );
                  }}
                  startMonth={getDateYearsFromNow(
                    -FeConstants.datePickerDefaultYearsOffset,
                  )}
                  endMonth={getDateYearsFromNow(
                    +FeConstants.datePickerDefaultYearsOffset,
                  )}
                />
              </div>
            </TooltipTrigger>
            {tooltipMessage && (
              <TooltipContent side="bottom">{tooltipMessage}</TooltipContent>
            )}
          </Tooltip>
        );

      case 'DateTime':
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <DateTimeInput
                  value={externalValue?.value ?? undefined}
                  disabled={disabled}
                  scrollContainer={scrollContainer}
                  onCommit={(date) =>
                    handleSetValue(externalProperty.id, date.toISOString())
                  }
                />
              </div>
            </TooltipTrigger>
            {tooltipMessage && (
              <TooltipContent side="bottom">{tooltipMessage}</TooltipContent>
            )}
          </Tooltip>
        );

      default:
        return null;
    }
  }, [
    externalProperty,
    owned,
    externalValue?.value,
    handleSetValue,
    disabled,
    showNotEditablePlaceholder,
    textareaHeight,
    onHeightChange,
    user,
    isDatePickerOpen,
    scrollContainer,
  ]);

  return (
    <StyledEPWrapper
      id={`${externalProperty.valueType}-${externalProperty.publicName}`}
      style={style}
    >
      <div className="form-box" style={formBoxStyle}>
        {showLabel && (
          <p className="text-sm font-semibold leading-5.5">
            {externalProperty.publicName}
            {externalProperty.isMandatory && '*'}:
          </p>
        )}
        {component}
      </div>
    </StyledEPWrapper>
  );
};

export default InputExternalPropertyNotTag;
